import React from 'react';
import { ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Card';
import { Badge } from '../Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../Table';
import { isKnownCorridor } from '../../data/transitCorridors';
import { currentTimeIn } from '../../utils/time';
import { ACCESS_METHOD_LABELS, type AccessMethod } from '../../utils/accessMethods';
import type { OriginCoverage, RestrictedAccessRow, RestrictedHit } from '../../types/traffic';
interface RestrictedJurisdictionsCardProps {
  /** Screening results computed across the full visitor population, in SQL. */
  hits: RestrictedHit[];
  listSize: number;
  coverage: OriginCoverage;
  uniqueVisitors: number;
  restrictedAccess: RestrictedAccessRow[];
}

/**
 * Traffic from jurisdictions the business cannot serve. Screening runs in the
 * database over every visitor — an earlier version cross-referenced the top-10
 * country lists in the browser, which silently hid any jurisdiction with only a
 * few visitors.
 */
export function RestrictedJurisdictionsCard({
  hits,
  listSize,
  coverage,
  uniqueVisitors,
  restrictedAccess
}: RestrictedJurisdictionsCardProps) {
  const isClean = hits.length === 0;
  const totalByIp = hits.reduce((sum, hit) => sum + hit.byIp, 0);
  const totalByRegion = hits.reduce((sum, hit) => sum + hit.byBrowserRegion, 0);
  const totalMasked = hits.reduce((sum, hit) => sum + hit.masked, 0);
  const flagged = new Set(hits.map(hit => hit.code));
  const methodTotals = new Map<string, number>();
  for (const row of restrictedAccess) {
    if (!flagged.has(row.origin.trim().toUpperCase())) continue;
    if (row.method === 'unknown') continue;
    methodTotals.set(row.method, (methodTotals.get(row.method) ?? 0) + row.visitors);
  }
  const methodsByOrigin = Array.from(methodTotals.entries()).sort((a, b) => b[1] - a[1]).map(([method, visitors]) => `${ACCESS_METHOD_LABELS[method as AccessMethod] ?? method} (${visitors})`);
  const share = (value: number) => uniqueVisitors > 0 ? Math.min(100, Math.round(value / uniqueVisitors * 100)) : 0;
  const screenedByRegion = Math.min(coverage.withOrigin, uniqueVisitors);
  const screenedByIp = Math.min(coverage.withCountry, uniqueVisitors);
  return <Card data-id="element-346" data-mp="2o1q1s68s1p4q">
      <CardHeader data-id="element-347" data-mp="2o1gamfbzxbxg">
        <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-348" data-mp="2o0y48pvmdhbt">
          <CardTitle className="flex items-center gap-2" data-id="element-349" data-mp="3brkb4dtw0vk8">
            <ShieldAlert className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-350" data-mp="3gp3ozcmob6bn" />
            Restricted jurisdictions
          </CardTitle>
          <Badge variant="outline" data-id="element-351" data-mp="32p1b01hhkx1o">Screening signal</Badge>
        </div>
        <CardDescription data-id="element-352" data-mp="36qehmdsel49n">
          Traffic from territories where the business cannot operate, screened across every visitor
          by IP country and by the region the browser&rsquo;s own settings imply
        </CardDescription>
      </CardHeader>
      <CardContent data-id="element-353" data-mp="1v9wl6dot31n0">
        {isClean ? <div className="text-xs leading-relaxed text-muted-foreground" data-id="element-354" data-mp="1nrp03qmbtlxm">
            <p className="font-medium text-foreground" data-id="element-355" data-mp="1itf092pnpx5o">No hits across {listSize} jurisdictions</p>
            <p className="mt-1.5" data-id="element-356" data-mp="1xqnvi7i2zl2o">
              {screenedByRegion.toLocaleString()} of {uniqueVisitors.toLocaleString()} visitors (
              {share(screenedByRegion)}%) were screened by browser region, which needs no IP lookup.{' '}
              {screenedByIp.toLocaleString()} were also screened by IP country (
              {share(screenedByIp)}%). Every visitor is checked individually, not just the top
              countries.
            </p>
            <p className="mt-1.5" data-id="element-357" data-mp="1etcy6sg0f1pv">
              Remaining blind spots: a visitor on Tor (time zone forced to UTC) or with their system
              set to another region resolves to no jurisdiction at all, and unresolved IPs cannot be
              screened on that axis.
            </p>
          </div> : <>
            <div className="grid grid-cols-3 gap-3 sm:gap-4" data-id="element-358" data-mp="hal9uop48ogi">
              <div data-id="element-359" data-mp="3grzxr3wrwvl7">
                <p className="font-mono text-xl tabular-nums text-foreground" data-id="element-360" data-mp="3goc506z5kd7j">
                  {totalByIp.toLocaleString()}
                </p>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-361" data-mp="3pqv3kevwo3nz">by IP country</p>
              </div>
              <div data-id="element-362" data-mp="3ui3czax8jp15">
                <p className="font-mono text-xl tabular-nums text-foreground" data-id="element-363" data-mp="3uiliyz9p9hm9">
                  {totalByRegion.toLocaleString()}
                </p>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-364" data-mp="8alpf77iif9z">by browser region</p>
              </div>
              <div data-id="element-365" data-mp="ccgn81q8iaj0">
                <p className="font-mono text-xl tabular-nums text-foreground" data-id="element-366" data-mp="cbytpcuyiaes">
                  {totalMasked.toLocaleString()}
                </p>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-367" data-mp="h97b3b8a6aez">masked — IP exits elsewhere</p>
              </div>
            </div>

            <div className="-mx-1 mt-6 overflow-x-auto px-1" data-id="element-368" data-mp="2j24yusn7act2">
              <Table data-id="element-369" data-mp="2j4r76uhx0wcc">
                <TableHeader data-id="element-370" data-mp="2bj5jqz6rv7fi">
                  <TableRow data-id="element-371" data-mp="2bk74xwhi5aay">
                    <TableHead data-id="element-372" data-mp="2bkgjmnx3jv1c">Jurisdiction</TableHead>
                    <TableHead data-id="element-373" data-mp="26rxac35fzm2c">Basis</TableHead>
                    <TableHead className="text-right" data-id="element-374" data-mp="26r49fit5zqs4">By IP</TableHead>
                    <TableHead className="text-right" data-id="element-375" data-mp="21udqmnpz6h30">By browser region</TableHead>
                    <TableHead data-id="element-376" data-mp="1wv0wdhwry7qe">Their local time now</TableHead>
                    <TableHead data-id="element-377" data-mp="1wseplkhpjw98">Masked via</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-id="element-378" data-mp="2piwzb4cxsvvm">
                  {hits.map(hit => <TableRow key={hit.code} data-id="element-379" data-mp="2pjpxvi1qsf2o">
                      <TableCell className="whitespace-nowrap text-xs text-foreground" data-id="element-380" data-mp="2kjan8y8uhloe">
                        {hit.name} ({hit.code})
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground" data-id="element-381" data-mp="2gkl0if1x2rs3">{hit.basis}</TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums" data-id="element-382" data-mp="192c1tgkttcgf">
                        {hit.byIp.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums" data-id="element-383" data-mp="16l0h4x3hp0rp">
                        {hit.byBrowserRegion.toLocaleString()}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs" data-id="element-384" data-mp="wl9m8ub0ojr3">
                        {hit.browserZones.length > 0 ? <span className="flex flex-col gap-0.5" data-id="element-385" data-mp="11n8mvwfdh2kc">
                            {hit.browserZones.map(zone => <span key={zone} className="font-mono" data-id="element-386" data-mp="skzgnir4zvcr">
                                <span className="text-foreground" data-id="element-387" data-mp="smaktjohv54e">{currentTimeIn(zone)}</span>{' '}
                                <span className="text-muted-foreground" data-id="element-388" data-mp="dtqiumd2z3oj">
                                  {zone.split('/').pop()?.replace(/_/g, ' ')}
                                </span>
                              </span>)}
                          </span> : <span className="font-mono text-muted-foreground" data-id="element-389" data-mp="3mdapqrgity3p">—</span>}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground" data-id="element-390" data-mp="3hfsoln8qwn69">
                        {hit.maskedVia.length > 0 ? <span className="flex flex-wrap items-center gap-1.5" data-id="element-391" data-mp="3vbw74ja9b72w">
                            {hit.maskedVia.map(via => <span key={via} className="whitespace-nowrap" data-id="element-392" data-mp="96925n8c1dcn">
                                {via}
                                {isKnownCorridor(hit.code, via) && <span className="ml-1 text-foreground" title="Known transit route" data-id="element-393" data-mp="hzvq4r6atcba">
                                    ◆
                                  </span>}
                              </span>)}
                          </span> : '—'}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 space-y-2 text-xs leading-relaxed text-muted-foreground" data-id="element-394" data-mp="1yaohj0iefnks">
              <p data-id="element-395" data-mp="23c41ep8ubo1l">
                <span className="font-mono text-foreground" data-id="element-396" data-mp="239ht2ne4l4ib">◆</span> marks a{' '}
                <span className="font-medium text-foreground" data-id="element-397" data-mp="28a5mtgtqnzg8">known transit route</span> — a country
                traffic from that jurisdiction commonly passes through, by cross-border roaming, a
                leased line into an adjacent hub, or a VPN endpoint in the nearest large market. A
                corridor match makes the pairing consistent with normal routing; it is not evidence
                about any person, who may simply live there.
              </p>
              {methodsByOrigin.length > 0 && <p data-id="element-398" data-mp="rul2h92am78u">
                  Access methods seen from these regions:{' '}
                  <span className="text-foreground" data-id="element-399" data-mp="wtfkw9wcltsf">{methodsByOrigin.join(' · ')}</span>
                </p>}
            </div>
          </>}

        <div className="mt-6 space-y-2 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground" data-id="element-400" data-mp="kdy0nfgogo20">
          <p className="font-medium text-foreground" data-id="element-401" data-mp="fcij3xdpkzod">This is not a sanctions control</p>
          <p data-id="element-402" data-mp="adp3nzev2ylk">
            &ldquo;By browser region&rdquo; comes from the visitor&rsquo;s time zone and language —
            settings anyone can change in seconds, and which are wrong for travellers, relocated
            machines, and corporate laptops imaged elsewhere. It is a directional indicator of where
            demand comes from, not a determination about any person.
          </p>
          <p data-id="element-403" data-mp="3nuoa64bgxj8g">
            Real sanctions screening belongs in authenticated onboarding and payments, against a
            provider with denied-party lists — not on a maintenance page. Use this card to size
            exposure and to see whether IP-based geoblocking has a gap, then verify there.
          </p>
          <p data-id="element-404" data-mp="5wdp3zuockbv">
            The list lives in the <span className="font-mono" data-id="element-405" data-mp="5vc4p4rrqlhh">restricted_jurisdictions</span> table
            and is a seed, not legal advice — programmes change and differ by regulator (OFAC, EU, UK
            OFSI, UN), so Compliance must own it.
          </p>
        </div>
      </CardContent>
    </Card>;
}